import { LAYOUT_CONFIG } from "@circle-react-uikit/CircleAIAppShell/Layout/defaults";
import type { ConversationPanelState } from "@circle-react-uikit/CircleAIAppShell/types";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { browserLocalStorage } from "@/react/lib/browserStorage";

interface OpenPanelOptions {
  avatarId?: number | null;
  chatId?: number | null;
  panelState?: Exclude<ConversationPanelState, "closed">;
}

interface SelectAvatarOptions {
  avatarId: number;
  chatId: number | null;
}

interface AvatarConversationPanelStore {
  activeChatId: number | null;
  closePanel: () => void;
  isWidgetCollapsed: boolean;
  newConversationAvatarId: number | null;
  openPanel: (options?: OpenPanelOptions) => void;
  panelState: ConversationPanelState;
  panelWidth: number;
  selectAvatar: (options: SelectAvatarOptions) => void;
  setActiveChatId: (chatId: number | null) => void;
  setPanelState: (state: ConversationPanelState) => void;
  setPanelWidth: (width: number) => void;
  showAvatars: () => void;
  startNewConversation: (avatarId: number) => void;
  toggleFullscreen: () => void;
  toggleWidgetCollapsed: () => void;
  view: "conversation" | "avatars";
}

export const useAvatarConversationPanelStore =
  create<AvatarConversationPanelStore>()(
    persist(
      set => ({
        activeChatId: null,
        isWidgetCollapsed: false,
        newConversationAvatarId: null,
        panelState: "closed",
        panelWidth: LAYOUT_CONFIG.conversationPanel.default,
        view: "conversation",
        openPanel: ({ avatarId = null, chatId, panelState = "default" } = {}) =>
          set({
            panelState,
            view: "conversation",
            ...(chatId !== undefined && {
              activeChatId: chatId,
              newConversationAvatarId: avatarId,
            }),
          }),
        closePanel: () => set({ panelState: "closed" }),
        selectAvatar: ({ avatarId, chatId }) =>
          set({
            activeChatId: chatId,
            newConversationAvatarId: chatId === null ? avatarId : null,
            view: "conversation",
          }),
        setActiveChatId: activeChatId =>
          set({ activeChatId, newConversationAvatarId: null }),
        setPanelState: panelState => set({ panelState }),
        setPanelWidth: w =>
          set(s => (s.panelWidth === w ? s : { panelWidth: w })),
        showAvatars: () => set({ view: "avatars" }),
        startNewConversation: avatarId =>
          set({ activeChatId: null, newConversationAvatarId: avatarId }),
        toggleFullscreen: () =>
          set(s => ({
            panelState:
              s.panelState === "fullscreen" ? "default" : "fullscreen",
          })),
        toggleWidgetCollapsed: () =>
          set(s => ({ isWidgetCollapsed: !s.isWidgetCollapsed })),
      }),
      {
        name: "circle-avatars-panel",
        storage: createJSONStorage(() => browserLocalStorage),
        version: 1,
        partialize: ({ activeChatId, isWidgetCollapsed }) => ({
          activeChatId,
          isWidgetCollapsed,
        }),
      },
    ),
  );
